/**
 * One `ai_prompt` value carries both AI flavours of a step:
 *
 *   { "simple": { "command": "...", "word": "...", "definition": "..." },
 *     "extended": { "command": "...", "purpose": "...", "program": { ... } } }
 *
 * The course AI mode decides which half runs. Anything stored before this
 * shape existed (a flat JSON object or plain text) is the extended half.
 */

export interface AiPromptSimple {
  command?: string;
  word?: string;
  definition?: string;
  [key: string]: unknown;
}

export interface AiPromptEnvelope {
  simple: AiPromptSimple;
  /** Extended prompt as authored text: JSON (pretty-printed) or plain text. */
  extended: string;
}

const ENVELOPE_KEYS = new Set(['simple', 'extended']);

function parseObject(raw: string): Record<string, unknown> | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Parsed envelope object when `raw` uses the combined shape, else null. */
export function parseEnvelope(raw?: string | null): Record<string, unknown> | null {
  const obj = parseObject(String(raw ?? ''));
  if (!obj) return null;
  const keys = Object.keys(obj);
  return keys.length > 0 && keys.every((k) => ENVELOPE_KEYS.has(k)) ? obj : null;
}

function extendedToText(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

export function readAiPrompt(raw?: string | null, aiAnnounce?: string | null): AiPromptEnvelope {
  const env = parseEnvelope(raw);
  if (env) {
    const simple =
      env['simple'] && typeof env['simple'] === 'object' && !Array.isArray(env['simple'])
        ? { ...(env['simple'] as AiPromptSimple) }
        : {};
    if (!simple.command && aiAnnounce) simple.command = aiAnnounce;
    return { simple, extended: extendedToText(env['extended']) };
  }
  return {
    simple: aiAnnounce ? { command: aiAnnounce } : {},
    extended: String(raw ?? ''),
  };
}

function simpleIsEmpty(simple: AiPromptSimple): boolean {
  return Object.values(simple).every((v) => v === undefined || v === null || String(v).trim() === '');
}

function compactSimple(simple: AiPromptSimple): AiPromptSimple {
  const out: AiPromptSimple = {};
  for (const [k, v] of Object.entries(simple)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && !v.trim()) continue;
    out[k] = typeof v === 'string' ? v.trim() : v;
  }
  return out;
}

/**
 * Stored string for an envelope. Without a simple half the extended text is
 * stored as-is, so readers that predate the combined shape keep working.
 */
export function writeAiPrompt(envelope: AiPromptEnvelope): string {
  const extendedText = (envelope.extended ?? '').trim();
  if (simpleIsEmpty(envelope.simple)) return extendedText;

  const out: Record<string, unknown> = { simple: compactSimple(envelope.simple) };
  if (extendedText) out['extended'] = parseObject(extendedText) ?? extendedText;
  return JSON.stringify(out, null, 2);
}

/** The part of a stored prompt that the given course AI mode runs on. */
export function promptForMode(raw?: string | null, mode?: string | null): string {
  const env = parseEnvelope(raw);
  if (!env) return String(raw ?? '');
  if (mode === 'simple') {
    const simple = env['simple'];
    return simple && typeof simple === 'object' ? JSON.stringify(simple) : '';
  }
  return extendedToText(env['extended']);
}
